import { AcademyApiError, academyRequest, browserSession, type BrowserSession } from "./academyApi";
import { notifyIdentitySessionChange } from "./generated/identitySession";
import type {
  IdentityAccess,
  IdentityProfile,
  PersonalIdentityProfile,
  UpdateIdentityProfile,
  Proof,
  RevokeOtherSessionsReceipt,
} from "./generated/native-identity";

type IdentityState = {
  session: BrowserSession | null;
  profile: PersonalIdentityProfile | null;
  security: IdentityProfile | null;
  access: IdentityAccess | null;
  loading: boolean;
  error: string;
};
let state: IdentityState = {
  session: null,
  profile: null,
  security: null,
  access: null,
  loading: false,
  error: "",
};
const listeners = new Set<() => void>();
let loading: Promise<void> | undefined;
let generation = 0;
function publish(patch: Partial<IdentityState>) {
  state = { ...state, ...patch };
  listeners.forEach(listener => listener());
}
export const identitySnapshot = () => state;
export function subscribeIdentity(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
export function bindIdentitySession(session: BrowserSession | null) {
  generation++;
  profileCommandKeys.clear();
  loading = undefined;
  publish({ session, profile: null, security: null, access: null, error: "", loading: false });
}
export function revalidateIdentitySession(session: BrowserSession) {
  publish({ session });
}
export async function checkIdentitySession() {
  const expected = state.session;
  const expectedGeneration = generation;
  if (!expected) throw new AcademyApiError(401, "Sign in to your Academy account.");
  let current: BrowserSession;
  try {
    current = await browserSession();
  } catch (error) {
    if (
      expectedGeneration === generation &&
      error instanceof AcademyApiError &&
      [401, 403].includes(error.status)
    ) {
      bindIdentitySession(null);
      publish({ error: "Your session has ended. Sign in again." });
    }
    throw error;
  }
  if (expectedGeneration !== generation)
    throw new AcademyApiError(409, "Your session changed while checking access.");
  if (
    current.user.id !== expected.user.id ||
    current.tenantId !== expected.tenantId ||
    current.workspace.id !== expected.workspace.id
  ) {
    bindIdentitySession(null);
    publish({ error: "Your account or workspace changed. Reload this workspace to continue." });
    throw new AcademyApiError(409, "Your account or workspace changed. Reload this workspace to continue.");
  }
  publish({ session: current });
  return current;
}
export function loadIdentity() {
  if (loading) return loading;
  const currentGeneration = generation;
  publish({ loading: true, error: "" });
  loading = (async () => {
    try {
      await checkIdentitySession();
      const [profile, security, access] = await Promise.all([
        academyRequest<PersonalIdentityProfile>("identity/profile"),
        academyRequest<IdentityProfile>("identity/me"),
        academyRequest<IdentityAccess>("identity/access"),
      ]);
      const session = await checkIdentitySession();
      if (currentGeneration !== generation) return;
      if (profile.email !== session.user.email || security.email !== profile.email)
        throw new Error("Account data changed while loading. Reload your account details.");
      publish({ profile, security, access });
    } catch (error) {
      if (currentGeneration === generation)
        publish({
          profile: null,
          security: null,
          access: null,
          error: error instanceof Error ? error.message : "Unable to load account details.",
        });
      throw error;
    } finally {
      if (currentGeneration === generation) {
        loading = undefined;
        publish({ loading: false });
      }
    }
  })();
  return loading;
}
const profileCommandKeys = new Map<string, string>();
export async function saveIdentityProfile(input: UpdateIdentityProfile) {
  const session = await checkIdentitySession();
  const fingerprint = JSON.stringify([session.tenantId, session.user.id, input]);
  const commandKey = profileCommandKeys.get(fingerprint) ?? crypto.randomUUID();
  if (profileCommandKeys.size >= 50 && !profileCommandKeys.has(fingerprint))
    profileCommandKeys.delete(profileCommandKeys.keys().next().value!);
  profileCommandKeys.set(fingerprint, commandKey);
  const result = await academyRequest<PersonalIdentityProfile>(
    "identity/profile",
    input,
    "PATCH",
    true,
    commandKey,
  );
  await checkIdentitySession();
  if (result.email !== session.user.email || result.version !== input.expectedVersion + 1)
    throw new Error("The profile save response could not be confirmed. Reload your profile before retrying.");
  profileCommandKeys.delete(fingerprint);
  publish({ profile: result, security: state.security ? { ...state.security, name: result.name } : null });
  return result;
}
export const identitySecurity = {
  async revokeOthers(expectedVersion: number, commandKey: string) {
    const current = await checkIdentitySession();
    const result = await academyRequest<RevokeOtherSessionsReceipt>(
      "identity/sessions/commands/revoke-others",
      { expectedVersion },
      "POST",
      true,
      commandKey,
    );
    if (
      result.value.accountId !== current.user.id ||
      result.value.identityVersion !== expectedVersion + 1 ||
      !Number.isInteger(result.value.revokedSessions) ||
      result.value.revokedSessions < 0
    )
      throw new Error(
        "The session revocation receipt could not be confirmed. Refresh security before retrying.",
      );
    notifyIdentitySessionChange();
    // A follow-up read must not turn a confirmed consequential command into a reported failure.
    void loadIdentity().catch(() => undefined);
    return result.value;
  },
  async enrol(currentPassword: string) {
    await checkIdentitySession();
    return academyRequest<{ secret: string; otpauthUri: string }>("identity/mfa/enrol", { currentPassword });
  },
  async confirm(code: string) {
    const session = await checkIdentitySession();
    const result = await academyRequest<{ recoveryCodes: string[] }>("identity/mfa/confirm", { code });
    notifyIdentitySessionChange();
    if (
      state.security &&
      state.session?.user.id === session.user.id &&
      state.session.tenantId === session.tenantId &&
      state.session.workspace.id === session.workspace.id
    )
      publish({
        security: {
          ...state.security,
          mfaEnabled: true,
          recoveryCodesRemaining: result.recoveryCodes.length,
        },
      });
    // Return one-time recovery codes before any fallible follow-up request.
    return result;
  },
  async manage(
    operation: "password/change" | "mfa/verify" | "mfa/recovery-codes" | "mfa/disable",
    proof: Proof & { newPassword?: string },
  ) {
    await checkIdentitySession();
    const result = await academyRequest<{
      completed: boolean;
      requiresSignIn: boolean;
      recoveryCodes?: string[];
    }>(`identity/${operation}`, proof);
    notifyIdentitySessionChange();
    if (result.requiresSignIn) bindIdentitySession(null);
    return result;
  },
  async revoke(sessionId: string) {
    await checkIdentitySession();
    const current = state.security?.currentSessionId === sessionId;
    await academyRequest(`identity/sessions/${encodeURIComponent(sessionId)}`, undefined, "DELETE");
    notifyIdentitySessionChange();
    if (current) bindIdentitySession(null);
    else await loadIdentity();
    return { requiresSignIn: current };
  },
  async refresh() {
    await academyRequest("browser-auth/refresh", {}, "POST", false);
    await loadIdentity();
  },
};

export async function selectIdentityWorkspace(workspaceId: string) {
  const current = await checkIdentitySession();
  const receipt = await academyRequest<{ workspaceId: string }>("applicant/workspaces/select", {
    workspaceId,
  });
  if (receipt.workspaceId !== workspaceId) throw new Error("The workspace change could not be confirmed.");
  const session = await browserSession();
  if (
    session.user.id !== current.user.id ||
    session.tenantId !== current.tenantId ||
    session.workspace.id !== workspaceId
  ) {
    bindIdentitySession(null);
    throw new Error("The account context changed. Sign in again.");
  }
  bindIdentitySession(session);
  notifyIdentitySessionChange();
}
